import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import type { OrganizationProfile } from '../../../../Generated/OrganizationProfile.ts'
import { useInvites } from '../../../../Flux/Selector/index.js'
import type { ILabInvite } from '../../../../Model/Invite/IInvite.js'
import Actions from '../../../../Flux/index.js'
import { isValidEmail } from '../../../../Util/EmailUtil.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { TrashIcon } from '@heroicons/react/24/outline'
import { OrganizationSearchWidget } from '../../../Component/Organization/OrganizationSearchWidget.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'

export interface InviteLabModalProps {
  orderId: string
}

export const InviteLabModal = ({ isOpen, orderId, onClose }: InviteLabModalProps & BaseModalProps) => {
  const dispatch = useDispatch()
  const invites = useInvites()

  const [email, setEmail] = useState('')
  const [selectedOrg, setSelectedOrg] = useState<OrganizationProfile | null>(null)
  const [additionalText, setAdditionalText] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  const existingLabInvites = invites !== 'LOADING' ? (invites.send?.labInvites?.filter((i: ILabInvite) => i.orderId === orderId) ?? []) : []

  useEffect(() => {
    if (isOpen) {
      dispatch(Actions.invitation.getAllInvitesRequest())
    }
  }, [isOpen, dispatch])

  const handleSubmit = () => {
    if (!selectedOrg && !isValidEmail(email)) return

    dispatch(
      Actions[orderSlice.name].inviteLabRequest({
        orderId,
        invite: {
          labEmail: selectedOrg ? undefined : email,
          organizationId: selectedOrg?.id,
          additionalText
        }
      })
    )

    setEmail('')
    setSelectedOrg(null)
    setAdditionalText('')
    onClose()
  }

  const handleDeleteInvite = (inviteId: number) => {
    dispatch(Actions.invitation.deleteInviteRequest({ inviteId }))
  }

  const searchWidget = () => (
    <div className="modal-box max-w-5xl">
      <h3 className="font-bold text-lg mb-4">Search Organization</h3>
      <OrganizationSearchWidget
        onOrganizationSelected={(org) => {
          setSelectedOrg(org)
          setEmail('')
          setShowSearch(false)
        }}
        pageSize={8}
        selectButtonText="Use This Organization"
      />
      <div className="modal-action mt-4">
        <button className="btn btn-outline" onClick={() => setShowSearch(false)}>
          Cancel
        </button>
      </div>
    </div>
  )

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      {showSearch ? (
        searchWidget()
      ) : (
        <>
          <div className={`modal-box w-fit`}>
            <h1 className="text-2xl text-primary font-bold">Invite Laboratory</h1>
            <p className="text-gray-600 mb-2">
              Invite a lab to evaluate this order. The lab will be notified via email and can accept or reject the order.
            </p>
            <hr className="my-2" />

            <h2 className="text-lg font-semibold mt-2 mb-1">Existing Invitations</h2>
            {invites === 'LOADING' && <p className="text-neutral-400">Loading...</p>}
            {invites !== 'LOADING' && existingLabInvites.length === 0 && <p className="text-neutral-400">No invitations sent yet.</p>}
            {existingLabInvites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between border-b py-1">
                <div className="text-sm text-gray-700">
                  to: <span className="font-medium">{invite.email || invite.invitedOrg?.name || 'Unknown'}</span>
                </div>
                <button className="btn btn-ghost btn-sm text-error" onClick={() => handleDeleteInvite(invite.id)} title="Delete Invitation">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}

            <hr className="my-4" />

            {!selectedOrg ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <button className="btn btn-outline btn-sm flex-1" onClick={() => setShowSearch(true)}>
                    🔍 Search Organization
                  </button>
                  <span className="text-gray-500 text-sm">or</span>
                  <input
                    data-testid={'lab-email'}
                    type="email"
                    className="input input-bordered flex-1"
                    placeholder="Enter lab email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (selectedOrg) setSelectedOrg(null)
                    }}
                  />
                </div>
                {email && !isValidEmail(email) && <p className="text-sm text-error mt-1">Please enter a valid email address.</p>}
              </>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-sm border mb-2">
                <div className="text-sm">
                  <span className="text-gray-600">Selected Organization:</span> <span className="font-semibold">{selectedOrg.name}</span>
                </div>
                <button
                  className="btn btn-sm btn-outline btn-error"
                  onClick={() => {
                    setSelectedOrg(null)
                    setEmail('')
                  }}
                >
                  ✕ Clear
                </button>
              </div>
            )}

            <label className="label mt-2">Additional Information</label>
            <textarea
              className="textarea textarea-bordered w-full"
              rows={4}
              value={additionalText}
              onChange={(e) => setAdditionalText(e.target.value)}
              placeholder="Optional message to the lab..."
            />

            <div className="mt-4 flex justify-end gap-2">
              <button className="btn btn-outline" onClick={() => onClose()}>
                Cancel
              </button>
              <button
                data-testid={'invite-lab-submit'}
                className="btn btn-primary"
                disabled={!selectedOrg && !isValidEmail(email)}
                onClick={handleSubmit}
              >
                Send Invite
              </button>
            </div>
          </div>
        </>
      )}
    </BaseModal>
  )
}
